import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { archiveRequestAPI } from '../api/api';
import './ArchiveRequests.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { ARCHIVE_REQUEST_STATUS_LABELS } from '../utils/constants';
import { onayla } from '../utils/onayla';

const ArchiveRequests = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    status: ''
  });

  const isAdmin = () => {
    if (!user) return false;
    const role = user.Role ?? user.role ?? '';
    return role === 'Admin';
  };

  useEffect(() => {
    if (token) {
      if (!isAdmin()) {
        // Redirect non-admins away
        navigate('/dashboard');
        return;
      }
      loadArchiveRequests();
    }
  }, [token, user]);

  useEffect(() => {
    applyFilters();
  }, [requests, filters]);

  const loadArchiveRequests = async () => {
    setLoading(true);
    try {
      const data = await archiveRequestAPI.getAll();
      setRequests(data);
      setFilteredRequests(data);
    } catch (error) {
      console.error('Error loading archive requests:', error);
      alert('Arşiv istekleri yüklenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...requests];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(request => {
        const foyNo = String(request.CaseFileFoyNo ?? request.caseFileFoyNo ?? '');
        const muvekkil = (request.CaseFileMuvekkil ?? request.caseFileMuvekkil ?? '').toLowerCase();
        const requester = (request.RequestedByUserName ?? request.requestedByUserName ?? '').toLowerCase();
        return foyNo.includes(searchLower) || 
               muvekkil.includes(searchLower) || 
               requester.includes(searchLower);
      });
    }

    if (filters.status) {
      filtered = filtered.filter(request => {
        const status = request.Status ?? request.status;
        return String(status) === filters.status;
      });
    }

    setFilteredRequests(filtered);
    setCurrentPage(1);
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApprove = async (requestId) => {
    if (!await onayla('Bu isteği onaylamak istediğinizden emin misiniz? Dosya arşivlenecektir.')) {
      return;
    }
    setLoading(true);
    try {
      await archiveRequestAPI.approve(requestId);
      await loadArchiveRequests();
      alert('İstek onaylandı ve dosya arşivlendi');
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (requestId) => {
    if (!await onayla('Bu isteği reddetmek istediğinizden emin misiniz?')) {
      return;
    }
    setLoading(true);
    try {
      await archiveRequestAPI.reject(requestId);
      await loadArchiveRequests();
      alert('İstek reddedildi');
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Export functions
  const exportToPDF = () => {
    try {
      const doc = new jsPDF('l', 'mm', 'a4');
      doc.setFontSize(16);
      doc.text('Arşiv İstekleri Listesi', 14, 15);
      
      const tableData = filteredRequests.map(request => [
        String(request.CaseFileFoyNo ?? request.caseFileFoyNo ?? ''),
        String(request.CaseFileMuvekkil ?? request.caseFileMuvekkil ?? '').substring(0, 30),
        String(request.RequestedByUserName ?? request.requestedByUserName ?? ''),
        ARCHIVE_REQUEST_STATUS_LABELS[request.Status ?? request.status] ?? '',
        request.CreatedDate ? new Date(request.CreatedDate ?? request.createdDate).toLocaleDateString('tr-TR') : ''
      ]);

      autoTable(doc, {
        head: [['Foy No', 'Müvekkil', 'İstek Eden', 'Durum', 'İstek Tarihi']],
        body: tableData,
        startY: 25,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [66, 139, 202], textColor: 255 }
      });

      doc.save('arsiv-istekleri.pdf');
    } catch (error) {
      console.error('PDF export error:', error);
      alert('PDF oluşturulurken hata oluştu');
    }
  };

  const exportToExcel = () => {
    try {
      const worksheetData = [
        ['Foy No', 'Müvekkil', 'İstek Eden', 'Durum', 'İstek Tarihi', 'Güncelleme Tarihi']
      ];

      filteredRequests.forEach(request => {
        worksheetData.push([
          request.CaseFileFoyNo ?? request.caseFileFoyNo ?? '',
          request.CaseFileMuvekkil ?? request.caseFileMuvekkil ?? '',
          request.RequestedByUserName ?? request.requestedByUserName ?? '',
          ARCHIVE_REQUEST_STATUS_LABELS[request.Status ?? request.status] ?? '',
          request.CreatedDate ? new Date(request.CreatedDate ?? request.createdDate).toLocaleDateString('tr-TR') : '',
          request.UpdatedDate ? new Date(request.UpdatedDate ?? request.updatedDate).toLocaleDateString('tr-TR') : ''
        ]);
      });

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Arşiv İstekleri');
      XLSX.writeFile(workbook, 'arsiv-istekleri.xlsx');
    } catch (error) {
      console.error('Excel export error:', error);
      alert('Excel oluşturulurken hata oluştu');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleCopy = () => {
    try {
      const text = filteredRequests.map(request => 
        `${request.CaseFileFoyNo ?? request.caseFileFoyNo}\t${request.CaseFileMuvekkil ?? request.caseFileMuvekkil}\t${ARCHIVE_REQUEST_STATUS_LABELS[request.Status ?? request.status]}`
      ).join('\n');
      
      navigator.clipboard.writeText(text).then(() => {
        alert('Kopyalandı!');
      });
    } catch (error) {
      alert('Kopyalama başarısız');
    }
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRequests.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);

  const formatDate = (date) => {
    if (!date) return '';
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return dateObj.toLocaleDateString('tr-TR');
    } catch (e) {
      return date;
    }
  };

  if (!isAdmin()) {
    return null; // Or show "Access Denied" message
  }

  return (
    <div className="archive-requests-page">
      <div className="page-header">
        <h1>Arşiv İstekleri Yönetimi</h1>
      </div>

      {/* Filter Panel */}
      <div className="filter-panel">
        <div className="filter-panel-left">
          <input
            type="text"
            placeholder="Ara (Foy No, Müvekkil, İstek Eden)..."
            className="form-control"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
          <select
            className="form-control"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">Tüm Durumlar</option>
            {Object.entries(ARCHIVE_REQUEST_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div className="filter-panel-right">
          <select 
            className="form-control"
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            style={{ minWidth: '80px' }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {/* Info Banner */}
      <div className="info-banner admin-banner">
        <strong>Admin Paneli:</strong> Tüm arşiv isteklerini görüntüleyebilir ve onaylayabilir/reddedebilirsiniz.
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading-state">Yükleniyor...</div>
      ) : (
        <>
          <div className="table-container">
            <table className="archive-requests-table">
              <thead>
                <tr>
                  <th>Foy No</th>
                  <th>Müvekkil</th>
                  <th>İstek Eden</th>
                  <th>Durum</th>
                  <th>İstek Tarihi</th>
                  <th>Güncelleme Tarihi</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="empty-table">
                      Henüz arşiv isteği bulunmamaktadır.
                    </td>
                  </tr>
                ) : (
                  currentItems.map((request) => {
                    const status = request.Status ?? request.status;
                    return (
                      <tr key={request.Id ?? request.id}>
                        <td>{request.CaseFileFoyNo ?? request.caseFileFoyNo ?? 'N/A'}</td>
                        <td>{request.CaseFileMuvekkil ?? request.caseFileMuvekkil ?? 'N/A'}</td>
                        <td>{request.RequestedByUserName ?? request.requestedByUserName ?? 'N/A'}</td>
                        <td>
                          <span className={`status-badge status-${status}`}>
                            {ARCHIVE_REQUEST_STATUS_LABELS[status] ?? 'Bilinmiyor'}
                          </span>
                        </td>
                        <td>{formatDate(request.CreatedDate ?? request.createdDate)}</td>
                        <td>{formatDate(request.UpdatedDate ?? request.updatedDate)}</td>
                        <td>
                          {status === 1 && (
                            <>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleApprove(request.Id ?? request.id)}
                                disabled={loading}
                              >
                                ✓ Onayla
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleReject(request.Id ?? request.id)}
                                disabled={loading}
                              >
                                ✗ Reddet
                              </button>
                            </>
                          )}
                          {status !== 1 && (
                            <span className="processed-badge">
                              {status === 2 ? '✓ Onaylandı' : '✗ Reddedildi'}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination-info">
            <p>
              {filteredRequests.length} kayıttan {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredRequests.length)} arasındaki kayıtlar gösteriliyor
              {requests.length !== filteredRequests.length && ` (Toplam ${requests.length} kayıttan filtrelendi)`}
            </p>
          </div>

          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              ‹
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            {totalPages > 5 && currentPage < totalPages - 2 && <span className="pagination-ellipsis">...</span>}
            {totalPages > 5 && (
              <button
                className={`pagination-btn ${currentPage === totalPages ? 'active' : ''}`}
                onClick={() => setCurrentPage(totalPages)}
              >
                {totalPages}
              </button>
            )}
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              ›
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ArchiveRequests;